/**
 * Admin → Overview: point-in-time usage metrics from GET /admin/overview.
 * Grouped into Users / Revenue / Engagement / Content cards.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getAdminOverview,
  getFunnel,
  type AdminOverview,
  type FunnelStats,
} from "@/features/admin/lib/adminApi";

export function OverviewView() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: getAdminOverview,
  });

  if (isLoading) {
    return <p className="text-sm text-text-muted">Loading metrics…</p>;
  }
  if (isError || !data) {
    return <p className="text-sm text-danger">Couldn't load metrics.</p>;
  }

  return (
    <div className="space-y-6">
      <FunnelPanel />

      <Card title="Users">
        <Stat label="Total" value={fmt(data.users.total)} />
        <Stat label="New · 7d" value={`+${fmt(data.users.new7d)}`} />
        <Stat label="New · 30d" value={`+${fmt(data.users.new30d)}`} />
        <Stat
          label="Active · 7d"
          value={fmt(data.engagement.activeUsers7d)}
          hint="distinct users with a coach response"
        />
      </Card>

      <Card title="Subscriptions & revenue">
        <Stat
          label="Est. MRR"
          value={`$${data.revenue.estimatedMrr.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
          hint="list price, excludes coupons"
        />
        <Stat label="Active subs" value={fmt(data.revenue.activeSubscriptions)} />
        <Stat label="Core" value={fmt(data.users.byPlan.core ?? 0)} />
        <Stat label="Plus" value={fmt(data.users.byPlan.plus ?? 0)} />
        <Stat label="Unlimited" value={fmt(data.users.byPlan.unlimited ?? 0)} />
        <Stat label="Past due" value={fmt(data.users.byStatus.past_due ?? 0)} />
        <Stat label="Canceled" value={fmt(data.users.byStatus.canceled ?? 0)} />
      </Card>

      <Card title="Engagement">
        <Stat
          label="Metered responses · today"
          value={fmt(data.engagement.coachResponsesToday)}
          hint="billable turns; excludes daily + library-served"
        />
        <Stat
          label="· this month"
          value={fmt(data.engagement.coachResponsesMonth)}
        />
        <Stat label="· all time" value={fmt(data.engagement.coachResponsesTotal)} />
        <Stat
          label="Conversations"
          value={fmt(data.engagement.conversationsTotal)}
          hint={`+${fmt(data.engagement.conversationsNew7d)} in 7d`}
        />
        <Stat label="Skill signals" value={fmt(data.engagement.skillSignalsTotal)} />
        <Stat
          label="Daily challenges · today"
          value={fmt(data.engagement.dailyCompletionsToday)}
        />
      </Card>

      <Card title="Content & queue">
        <Stat label="Library spots" value={fmt(data.content.librarySpots)} />
        <Stat
          label="Under review"
          value={fmt(data.content.underReviewSpots)}
          hint="reported spots awaiting triage"
        />
        <Stat
          label="Feedback · unread"
          value={fmt(data.content.feedbackUnread)}
          hint={`${fmt(data.content.feedbackTotal)} total`}
        />
      </Card>

      <p className="text-xs text-text-muted">
        Snapshot at {new Date(data.generatedAt).toLocaleString()}.
      </p>
    </div>
  );
}

const FUNNEL_WINDOWS = [7, 30, 90] as const;

function FunnelPanel() {
  const [days, setDays] = useState<number>(30);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-funnel", days],
    queryFn: () => getFunnel(days),
  });

  return (
    <section className="rounded-2xl border border-border bg-surface p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">
          Conversion funnel
        </h2>
        <div className="flex gap-1 rounded-lg bg-bg p-0.5">
          {FUNNEL_WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                days === w
                  ? "bg-accent text-white font-semibold"
                  : "text-text-muted hover:text-text"
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p className="text-sm text-text-muted">Loading funnel…</p>}
      {(isError || !data) && !isLoading && (
        <p className="text-sm text-danger">Couldn't load funnel.</p>
      )}
      {data && <FunnelBars data={data} />}
    </section>
  );
}

function FunnelBars({ data }: { data: FunnelStats }) {
  // Bar widths are scaled to the cohort (the top of the funnel) so each
  // stage reads as a fraction of "everyone who signed up." Drop-off % is
  // relative to the PREVIOUS stage — that's where to look for leaks.
  const top = Math.max(data.cohortSize, 1);
  if (data.cohortSize === 0) {
    return (
      <p className="text-sm text-text-muted">
        No signups in the last {data.windowDays} days.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {data.stages.map((stage, i) => {
        const prev = i > 0 ? data.stages[i - 1].count : null;
        const fromPrev =
          prev && prev > 0 ? Math.round((stage.count / prev) * 100) : null;
        const ofTop = Math.round((stage.count / top) * 100);
        return (
          <div key={stage.key} className="flex items-center gap-3">
            <div className="w-32 shrink-0 text-xs text-text-muted text-right">
              {stage.label}
            </div>
            <div className="flex-1 h-7 rounded-md bg-bg overflow-hidden">
              <div
                className="h-full bg-accent/70 rounded-md transition-all"
                style={{ width: `${ofTop}%` }}
              />
            </div>
            <div className="w-24 shrink-0 flex items-baseline gap-1.5">
              <span className="text-sm font-bold text-text tabular-nums">
                {stage.count.toLocaleString()}
              </span>
              {fromPrev !== null && (
                <span
                  className={`text-[11px] tabular-nums ${
                    fromPrev >= 50 ? "text-text-muted" : "text-brand"
                  }`}
                >
                  {fromPrev}%
                </span>
              )}
            </div>
          </div>
        );
      })}
      <p className="text-[11px] text-text-muted/70 pt-1">
        Cohort = {data.cohortSize.toLocaleString()} users who signed up in the
        last {data.windowDays}d. % is conversion from the previous stage.
        Recent signups haven't had time to convert, so late stages read low.
      </p>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-5">
      <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide mb-4">
        {title}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{children}</div>
    </section>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div>
      <div className="text-2xl font-bold text-text tabular-nums tracking-tight">
        {value}
      </div>
      <div className="text-xs text-text-muted mt-0.5">{label}</div>
      {hint && <div className="text-[11px] text-text-muted/70 mt-0.5">{hint}</div>}
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString();
}

export type { AdminOverview };
